/** Colors are stored as lowercase hex without `#` (`rrggbb` / `rrggbbaa`) or `transparent`. */
export interface GraphColors {
    // Shared
    background: string;
    text: string;
    border: string;
    label: string;
    count: string;
    // Heatmap
    empty: string;
    color1: string;
    color2: string;
    color3: string;
    color4: string;
    // Line graph
    line: string;
    point: string;
    pointBorder: string;
    area: string;
    grid: string;
    // Combo graph
    bar: string;
}

export const PRESET_THEMES = {
    light: {
        background: 'ffffff',
        text: '1f2328',
        border: 'd0d7de',
        label: '656d76',
        count: '1f2328',
        empty: 'ebedf0',
        color1: '9be9a8',
        color2: '40c463',
        color3: '30a14e',
        color4: '216e39',
        line: '2da44e',
        point: '1a7f37',
        pointBorder: 'ffffff',
        area: '2da44e',
        grid: '8c959f',
        bar: '54aeff',
    },
    dark: {
        background: '0d1117',
        text: 'e6edf3',
        border: '30363d',
        label: '8d96a0',
        count: 'e6edf3',
        empty: '161b22',
        color1: '0e4429',
        color2: '006d32',
        color3: '26a641',
        color4: '39d353',
        line: '39d353',
        point: '39d353',
        pointBorder: '0d1117',
        area: '39d353',
        grid: '8d96a0',
        bar: '1f6feb',
    },
    dim: {
        background: '22272e',
        text: 'adbac7',
        border: '444c56',
        label: '768390',
        count: 'adbac7',
        empty: '2d333b',
        color1: '0e4429',
        color2: '006d32',
        color3: '26a641',
        color4: '39d353',
        line: '57ab5a',
        point: '6bc46d',
        pointBorder: '22272e',
        area: '57ab5a',
        grid: '768390',
        bar: '4184e4',
    },
    lime: {
        background: '070d09',
        text: 'e3f2d0',
        border: '1d2a21',
        label: '8a9a8d',
        count: 'c5e88a',
        empty: '111c14',
        color1: '2c4a12',
        color2: '4d7a1a',
        color3: '7fb324',
        color4: 'a8d82e',
        line: 'a8d82e',
        point: 'a8d82e',
        pointBorder: '070d09',
        area: 'a8d82e',
        grid: '505a52',
        bar: '3d6b17',
    },
    ocean: {
        background: '0b1526',
        text: 'dbe7ff',
        border: '1f3354',
        label: '7d93b8',
        count: 'dbe7ff',
        empty: '14223a',
        color1: '12406e',
        color2: '1b6bb0',
        color3: '3b94e8',
        color4: '8cc8ff',
        line: '5cb3ff',
        point: '8cc8ff',
        pointBorder: '0b1526',
        area: '3b94e8',
        grid: '7d93b8',
        bar: '1b6bb0',
    },
    ember: {
        background: '1a1210',
        text: 'f5e6dc',
        border: '3b2a24',
        label: 'a8918a',
        count: 'f5e6dc',
        empty: '2a1d19',
        color1: '5c2a12',
        color2: '9a3f12',
        color3: 'e0661f',
        color4: 'ffb86b',
        line: 'ff9a3c',
        point: 'ffb86b',
        pointBorder: '1a1210',
        area: 'e0661f',
        grid: 'a8918a',
        bar: '9a3f12',
    },
    graphite: {
        background: '191a1e',
        text: 'e6e8eb',
        border: '2b2d33',
        label: '8d9199',
        count: 'c3c7ce',
        empty: '24262b',
        color1: '0c3d6e',
        color2: '075fb4',
        color3: '0b84f3',
        color4: '66b5ff',
        line: '0a4fc4',
        point: '4d9dff',
        pointBorder: '191a1e',
        area: '0b84f3',
        grid: '8d9199',
        bar: '0b84f3',
    },
} satisfies Record<string, GraphColors>;

export type PresetThemeName = keyof typeof PRESET_THEMES;
/** `custom` starts from the light palette and expects explicit colors. */
export type ThemeName = PresetThemeName | 'custom';

export const THEME_NAMES: ThemeName[] = [
    ...(Object.keys(PRESET_THEMES) as PresetThemeName[]),
    'custom',
];

export const CUSTOM_BASE_THEME: PresetThemeName = 'light';

export const themeColors = (theme: ThemeName): GraphColors => ({
    ...PRESET_THEMES[theme === 'custom' ? CUSTOM_BASE_THEME : theme],
});
